// src/utils/wheelUtils.ts
import * as d3 from 'd3';
import { MOTION } from '../../design-system/tokens';
import { WheelNode } from '../../types/geneCodes/geneticWheel';
import { getNodeColor } from './wheelColors';

// A4: buildWheelData (and its only consumer, calculateNodeRiskLevel) DELETED — zero importers; the
// live derivation is wheelGeneRing.geneRingForTrait, consumed inside each wheel's own builder.

/**
 * Creates a D3 hierarchy from wheel data and applies the partition layout
 */
export const createPartitionLayout = (data: WheelNode, radius: number): d3.HierarchyRectangularNode<WheelNode> => {
	// Create hierarchy from data
	const hierarchy = d3.hierarchy(data)
		.sum(d => d.value || 0)
		.sort((a, b) => (b.value || 0) - (a.value || 0));

	// Apply partition layout
	// Note: Using r² for area proportionality 
	const partition = d3.partition<WheelNode>()
		.size([2 * Math.PI, radius * radius]);

	return partition(hierarchy);
};

/**
 * Creates an arc generator for the sunburst visualization
 */
export const createArcGenerator = (innerRadiusFactor: number = 0) => {
	return d3.arc<d3.HierarchyRectangularNode<WheelNode>>()
		.startAngle(d => d.x0)
		.endAngle(d => d.x1)
		.padAngle(d => Math.min((d.x1 - d.x0) / 2, 0.005))
		.padRadius(1.5)
		.innerRadius(d => Math.max(0, Math.sqrt(d.y0) || innerRadiusFactor))
		.outerRadius(d => Math.max(0, Math.sqrt(d.y1 || 0)));
};

// getNodeColor lives in wheelColors so d3-free callers can import it; re-exported here so existing imports keep working.
export { getNodeColor };

/** MOTION.duration as numbers, because a d3 `.duration()` takes ms and the token is a CSS string. */
export const MOTION_MS = {
	instant: Number.parseInt(MOTION.duration.instant, 10),
	fast: Number.parseInt(MOTION.duration.fast, 10),
	normal: Number.parseInt(MOTION.duration.normal, 10),
	slow: Number.parseInt(MOTION.duration.slow, 10),
} as const;

/** A d3 transition ignores the CSS media query, so reduced motion has to zero the duration here. */
export const motionDuration = (ms: number): number => (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : ms);

/** Design-system §4: nothing below 10px, chart labels included. */
export const MIN_LABEL_FONT_PX = 10;

/** Mean glyph advance as a fraction of font size — SVG offers no cheap measurement inside a d3 callback. */
const GLYPH_RATIO = 0.58;

/** Characters that fit in `width` px at `fontSize`. */
export const charsFor = (fontSize: number, width: number): number => Math.floor(width / (fontSize * GLYPH_RATIO));

/** Fewer than this and the truncation is unreadable, so the label is dropped instead. */
const MIN_LABEL_CHARS = 6;

/** Glyph height as a multiple of font size — below this the neighbouring labels genuinely touch. */
const GLYPH_HEIGHT_RATIO = 0.9;

/** White space kept between the two halves of a staggered ring. */
const STAGGER_GAP = 6;

const LABEL_FONT_LADDER = [14, 12, 11, MIN_LABEL_FONT_PX];

/** Below this a bigger font is a worse label — an abbreviation nobody can read. */
const MIN_FIT_CHARS = 9;

/**
 * Calculate optimal position for label placement
 */
export const getLabelTransform = (d: d3.HierarchyRectangularNode<WheelNode>): string => {
	// Calculate angle for the center of the arc
	const angle = (d.x0 + d.x1) / 2;

	// Calculate radius for label placement
	const labelRadius = (Math.sqrt(d.y0) + Math.sqrt(d.y1)) / 2 * 0.9;

	// Convert to Cartesian coordinates
	const x = labelRadius * Math.sin(angle);
	const y = -labelRadius * Math.cos(angle);

	// Return the transform string
	return `translate(${x},${y})`;
};

/**
 * Truncate a label to a character budget, ending on the ellipsis character.
 */
export const truncateLabel = (name: string, maxChars: number): string => {
	if (maxChars < MIN_LABEL_CHARS) return '';
	if (name.length <= maxChars) return name;
	return `${name.slice(0, maxChars - 1).trimEnd()}…`;
};

/** Chord of the hub a title may occupy, leaving the glyphs clear of the circle's edge. */
export const CENTRE_TITLE_FIT = 0.9;

/**
 * Sets a wheel's centre title, shrinking the font before abbreviating and measuring the glyphs that
 * are actually drawn — a per-character estimate over-states upper case badly enough to truncate a
 * title that fits. Returns 0 width under jsdom, which keeps the untruncated text.
 */
export const applyCentreTitle = (node: SVGTextElement | null | undefined, name: string, maxWidth: number, maxFontSize: number): void => {
	// Guarded rather than assumed: the wheel suites drive d3 through a recorder stub, not the DOM.
	if (!node || typeof node.setAttribute !== 'function' || typeof node.getComputedTextLength !== 'function') return;
	const text = (name ?? '').toUpperCase();
	node.textContent = text;
	const drawnWidth = () => node.getComputedTextLength();

	for (let size = maxFontSize; size >= MIN_LABEL_FONT_PX; size -= 1) {
		node.setAttribute('font-size', `${size}px`);
		if (drawnWidth() <= maxWidth) return;
	}
	for (let chars = text.length - 1; chars >= MIN_LABEL_CHARS; chars -= 1) {
		node.textContent = truncateLabel(text, chars);
		if (drawnWidth() <= maxWidth) return;
	}
};

/** Width of a glyph run, the inverse of charsFor. */
export const labelWidth = (fontSize: number, chars: number): number => chars * fontSize * GLYPH_RATIO;

export interface ArcLabelFit {
	fontSize: number;
	text: string;
	/** Radial shift off the band centre. Neighbours on a cramped ring get opposite shifts. */
	offset: number;
}

export interface ArcLabelRing {
	/** Room along the label's baseline. */
	run: number;
	/** Radial thickness of the ring, which is what a stagger has to fit two labels into. */
	band: number;
	/** Angular width of the arc, in radians. */
	arcWidth: number;
	/** Radius the label sits at: the band centre, or the anchor radius when `anchored`. */
	radius: number;
	/** Position within the ring — only its parity is used. */
	index: number;
	/** True when the label starts at a fixed radius rather than being centred on the band. */
	anchored?: boolean;
}

/** Largest ladder size at which `chars` characters fit in `run`, else the legibility floor. */
export const fontForChars = (chars: number, run: number): number => LABEL_FONT_LADDER.find((size) => charsFor(size, run) >= chars) ?? MIN_LABEL_FONT_PX;

/** Tangential gap a label needs at its innermost end before neighbours touch. */
export const labelSpacingRadius = (fontSize: number, arcWidth: number): number => (arcWidth > 0 ? (fontSize * GLYPH_HEIGHT_RATIO) / arcWidth : 0);

/**
 * The label an arc carries, its size, and where to sit it. EVERY named arc gets a label — a segment
 * is never left blank. Where neighbours would collide the label is halved and staggered to the
 * opposite side of the band, so the two sit at different radii instead of on top of each other.
 */
export const fitArcLabel = (name: string, ring: ArcLabelRing): ArcLabelFit => {
	const fontSize = LABEL_FONT_LADDER.find((size) => charsFor(size, ring.run) >= MIN_FIT_CHARS) ?? MIN_LABEL_FONT_PX;
	if (!name) return { fontSize, text: '', offset: 0 };

	// Radial labels splay apart outward, so they are closest at the INNERMOST end of the glyph run.
	const full = truncateLabel(name, Math.max(MIN_LABEL_CHARS, charsFor(fontSize, ring.run)));
	const innerRadius = ring.anchored ? ring.radius : ring.radius - labelWidth(fontSize, full.length) / 2;
	const cramped = ring.arcWidth * Math.max(innerRadius, 0) < fontSize * GLYPH_HEIGHT_RATIO;
	const room = cramped ? Math.max(0, ring.band - STAGGER_GAP) / 2 : ring.run;
	const chars = Math.max(MIN_LABEL_CHARS, charsFor(fontSize, Math.min(ring.run, room)));
	const text = truncateLabel(name, chars);

	if (!cramped) return { fontSize, text, offset: 0 };

	const width = labelWidth(fontSize, text.length);
	const shift = Math.min((ring.band - width) / 2, width / 2 + STAGGER_GAP / 2);
	if (ring.anchored) return { fontSize, text, offset: ring.index % 2 ? width + STAGGER_GAP : 0 };
	return { fontSize, text, offset: (ring.index % 2 ? 1 : -1) * Math.max(0, shift) };
};

/**
 * Find a node in the hierarchy by ID
 */
export const findNodeById = (
	root: d3.HierarchyRectangularNode<WheelNode>,
	id: string
): d3.HierarchyRectangularNode<WheelNode> | null => {
	if (root.data.id === id) return root;

	if (!root.children) return null;

	for (const child of root.children) {
		const found = findNodeById(child, id);
		if (found) return found;
	}

	return null;
};

/**
 * Create a breadcrumb path for a given node
 */
export const createBreadcrumbPath = (
	node: d3.HierarchyRectangularNode<WheelNode>
): { name: string, node: d3.HierarchyRectangularNode<WheelNode> }[] => {
	const path: { name: string, node: d3.HierarchyRectangularNode<WheelNode> }[] = [];
	let current: d3.HierarchyRectangularNode<WheelNode> | null = node;

	while (current) {
		path.unshift({
			name: current.data.name,
			node: current
		});
		current = current.parent;
	}

	return path;
};

/**
 * Initialize animation properties on nodes
 */
export const initializeAnimationProperties = (root: d3.HierarchyRectangularNode<WheelNode>): void => {
	root.eachBefore(d => {
		(d as any).current = {
			x0: d.x0,
			x1: d.x1,
			y0: d.y0,
			y1: d.y1
		};
	});
};

/**
 * Apply tween animation for smooth transitions between states
 */
export const tweenArc = (d: d3.HierarchyRectangularNode<WheelNode>, arc: d3.Arc<any, any>) => {
	const nodeWithCurrent = d as d3.HierarchyRectangularNode<WheelNode> & { current?: { x0: number, x1: number, y0: number, y1: number } };
	const i = d3.interpolate(
		{ x0: nodeWithCurrent.current?.x0 || nodeWithCurrent.x0, x1: nodeWithCurrent.current?.x1 || nodeWithCurrent.x1, y0: nodeWithCurrent.current?.y0 || nodeWithCurrent.y0, y1: nodeWithCurrent.current?.y1 || nodeWithCurrent.y1 },
		{ x0: nodeWithCurrent.x0, x1: nodeWithCurrent.x1, y0: nodeWithCurrent.y0, y1: nodeWithCurrent.y1 }
	);

	return (t: number) => {
		const b = i(t);
		(d as any).current = b;
		return arc(b);
	};
};

/**
 * Calculate font size based on arc width
 */
export const calculateFontSize = (arcWidth: number): string => {
	if (arcWidth < 0.1) return "8px";
	if (arcWidth < 0.2) return "10px";
	return "12px";
};

/**
 * Determines if an arc is visible (large enough to display)
 */
export const arcVisible = (d: { y0: number, y1: number, x0: number, x1: number }): boolean => {
	return d.y1 <= 3 && d.y0 >= 1 && d.x1 > d.x0;
};

/**
 * Determines if a label should be displayed (arc is large enough for text)
 */
export const labelVisible = (d: { y0: number, y1: number, x0: number, x1: number }): boolean => {
	return d.y1 <= 3 && d.y0 >= 1 && (d.y1 - d.y0) * (d.x1 - d.x0) > 0.03;
};

/**
 * Helper to animate zoom-in/zoom-out transitions
 */
export const animateZoom = (
	container: d3.Selection<SVGGElement, unknown, null, undefined>,
	fromNode: d3.HierarchyRectangularNode<WheelNode> | null,
	toNode: d3.HierarchyRectangularNode<WheelNode>,
	arc: d3.Arc<any, any>,
	duration: number = 750
): void => {
	if (!fromNode) return;

	// Fade out existing elements
	container.selectAll("path, text")
		.transition()
		.duration(duration / 2)
		.style("opacity", 0)
		.remove();

	// Schedule render of new elements
	setTimeout(() => {
		// Create paths for the new view
		const paths = container.selectAll("path")
			.data(toNode.children || [])
			.enter()
			.append("path")
			.attr("fill", d => getNodeColor(d.data, false))
			.attr("d", arc)
			.style("opacity", 0);

		// Fade in with animation
		paths.transition()
			.duration(duration / 2)
			.style("opacity", 0.8);

		// Add center circle
		const centerCircle = container.append("circle")
			.attr("r", 0)
			.attr("fill", getNodeColor(toNode.data, false))
			.attr("stroke", "#fff")
			.attr("stroke-width", 2)
			.style("opacity", 0);

		centerCircle.transition()
			.duration(duration / 2)
			.attr("r", Math.sqrt(toNode.y0) / 2)
			.style("opacity", 1);
	}, duration / 2);
};